/**
 * Timeline des cee_events pour la page detail.
 * Liste chronologique inverse (plus recent en haut), expand pour voir tout.
 *
 * Affichage :
 *  - icone color par event_type
 *  - libelle court humain
 *  - meta (qui, payload condense)
 *  - timestamp relatif (timeAgo)
 *
 * @param {{
 *   events: Array<object>,
 *   loading?: boolean,
 *   limit?: number,
 * }} props
 */

import { useState } from 'react';
import { timeAgo } from '../lib/formatBytes.js';

const EVENT_LABELS = {
  dossier_created: { label: 'Dossier cree', color: 'bg-blue-50 text-blue-700' },
  doc_received: { label: 'Doc recu', color: 'bg-blue-50 text-blue-700' },
  doc_validated: { label: 'Doc valide', color: 'bg-emerald-50 text-emerald-700' },
  doc_rejected: { label: 'Doc rejete', color: 'bg-red-50 text-red-700' },
  doc_superseded: { label: 'Doc remplace', color: 'bg-gray-50 text-gray-700' },
  devis_generated: { label: 'Devis genere', color: 'bg-purple-50 text-purple-700' },
  ah_generated: { label: 'AH genere', color: 'bg-purple-50 text-purple-700' },
  sent_to_mandataire: { label: 'Transmis Loris', color: 'bg-emerald-50 text-emerald-700' },
  status_changed: { label: 'Status change', color: 'bg-orange-50 text-orange-700' },
  note_added: { label: 'Note ajoutee', color: 'bg-gray-50 text-gray-700' },
  reminder_sent: { label: 'Relance envoyee', color: 'bg-blue-50 text-blue-700' },
  document_downloaded: { label: 'Doc telecharge', color: 'bg-gray-50 text-gray-700' },
};

/** @param {object} ev @returns {string} */
function describePayload(ev) {
  const p = ev.payload || {};
  if (ev.eventType === 'status_changed' && p.old && p.new) {
    return `${p.old} -> ${p.new}`;
  }
  if (ev.eventType === 'doc_received' && p.docType) return `Type: ${p.docType}`;
  if (ev.eventType === 'doc_rejected' && p.reason) return p.reason;
  if (ev.eventType === 'note_added' && p.snippet) return p.snippet;
  return '';
}

export default function EventTimeline({ events, loading = false, limit = 10 }) {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <div className="text-center py-6 text-sm text-[#94a3b8]">Chargement...</div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-[#94a3b8]">Aucun evenement</div>
    );
  }

  const visible = expanded ? events : events.slice(0, limit);

  return (
    <div className="space-y-3">
      {visible.map((ev) => {
        const meta = EVENT_LABELS[ev.eventType] || { label: ev.eventType, color: 'bg-gray-50 text-gray-700' };
        const detail = describePayload(ev);
        return (
          <div key={ev.id} className="flex gap-3 items-start">
            <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-md font-semibold whitespace-nowrap ${meta.color}`}>
              {meta.label}
            </span>
            <div className="flex-1 min-w-0">
              {detail && <div className="text-sm text-[#475569] truncate" title={detail}>{detail}</div>}
              <div className="text-[11px] text-[#94a3b8]">
                {timeAgo(ev.createdAt)}{ev.actorUserName ? ` · ${ev.actorUserName}` : ''}
              </div>
            </div>
          </div>
        );
      })}
      {events.length > limit && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-blue-600 hover:underline"
        >
          {expanded ? 'Reduire' : `Voir les ${events.length - limit} autres`}
        </button>
      )}
    </div>
  );
}
